import { NonIdealState } from "@blueprintjs/core";
import {
  FGCustomPanel,
  FGListen,
  FGSelectInput,
  FGTextAreaInput,
  FieldGroup,
  FieldSet,
  InlineButtonContainer,
} from "nsitools-react";
import * as React from "react";
import { useHistory, useParams } from "react-router";
import * as Yup from "yup";

import {
  BlockedHealthTestDto,
  DogApi,
  ELab,
  ERequestState,
  HealthTestRequestApi,
  HealthTestRequestDto,
  PersonApi,
  RequestApi,
  RequestDto,
  RequestInfoDto,
} from "../../../../api";
import {
  ButtonsBloc,
  CancelButton,
  FGDogSearchInput,
  FGPeopleSearchInput,
  FGSRSHCheckboxInput,
  FGSRSHDateMaskInput,
  InformationTooltip,
  SharepointButton,
  SmallFormGenerator,
} from "../../../../components";
import { useDialog, useEventsContext } from "../../../../contexts";
import { ERoutes } from "../../../../ERoutes";
import { useApiService, useCrudApi, useTl, useWindow } from "../../../../hooks";
import { useReferential } from "../../../../hooks/useReferential";
import { ETLCodes } from "../../../../locales";
import { HealthPacksList } from "./HealthPacksList";
import { HealthTestList } from "./HealthTestList";

export interface IHealthTestRequestDetailProps {
  healthTestRequestId: number;
  editMode: boolean;
  requestInfo?: RequestInfoDto;
}

export const HealthTestRequestDetail: React.FunctionComponent<IHealthTestRequestDetailProps> = ({
  healthTestRequestId,
  editMode,
  requestInfo,
}) => {
  const api = useApiService(HealthTestRequestApi);
  const dogApi = useApiService(DogApi);
  const personApi = useApiService(PersonApi);
  /* ========== Hooks and needed variables ===========*/
  const { t } = useTl();
  const { showDialogPromise } = useDialog();
  const history = useHistory();
  const { openNewTab } = useWindow();

  const [dogIdValue, setDogIdValue] = React.useState<number>(0);
  const [lab, setLab] = React.useState<ELab>();

  const { masterRequestId = "0" } = useParams<{
    masterRequestId: string;
  }>();

  /* ========== Referentials ===========*/
  const [labs, lloading] = useReferential((a) => a.getLabs(), false);
  const [blockedIds, setBlockedIds] = React.useState<BlockedHealthTestDto>(null);
  const [labStatuses, lsloading] = useReferential((a) => a.getLabStatuses(), false);

  /* ========== Getting Data ===========*/
  const { data, loading, saving, saveItem, refresh } = useCrudApi<HealthTestRequestDto>(
    React.useMemo(
      () => ({
        getApiFn: (id = healthTestRequestId) => {
          if (id > 0) {
            return api.get(id);
          } else if (+masterRequestId > 0) {
            return api.getSubRequest(+masterRequestId);
          } else {
            return new HealthTestRequestDto({
              request: new RequestDto({
                requestDate: new Date(),
                status: ERequestState.REQUESTED,
                remark: "",
              }),
              healthPackIds: [],
            }).toJSON();
          }
        },
        saveApiFn: (d: HealthTestRequestDto) => api.save(d),
        onSavedRoute: (d: HealthTestRequestDto) => `${ERoutes.healthTestRequest}/${d.healthTestRequestId}/detail/edit`,
      }),
      [healthTestRequestId, masterRequestId, api]
    ),
    [healthTestRequestId]
  );

  const onRequestRefreshEvent = React.useCallback(() => {
    refresh();
  }, [refresh]);

  const { subscribeToEvent, unsubscribeEvent } = useEventsContext();
  React.useEffect(() => {
    subscribeToEvent("RequestRefresh", onRequestRefreshEvent);
    return () => unsubscribeEvent("RequestRefresh", onRequestRefreshEvent);
  }, [onRequestRefreshEvent, subscribeToEvent, unsubscribeEvent]);

  const [saveChecking, setSaveChecking] = React.useState(false);
  const saveCheck = React.useCallback(
    async (values) => {
      setSaveChecking(true);
      let isBanned = false;
      if (healthTestRequestId <= 0) {
        isBanned = (await personApi.isPersonExcludedQuery(values?.request?.personId)).value;
      }
      if (isBanned) {
        const isbannedValidation = await showDialogPromise({
          message: t(ETLCodes.PersonIsBannedConfirmMessage),
          title: t(ETLCodes.Warning),
        });
        if (isbannedValidation !== "yes") return;
      }
      setSaveChecking(false);
      await saveItem(values);
    },
    [healthTestRequestId, personApi, saveItem, showDialogPromise, t]
  );

  const [whiteTest, setWhiteTest] = React.useState(data?.whiteTest);

  /* ========== Yup ===========*/
  const RequestedFormSchema = React.useMemo(() => {
    return Yup.object().shape({
      dogId: Yup.number()
        .nullable()
        .when("whiteTest", (value, schema) => {
          return value ? schema.nullable() : schema.required(t(ETLCodes.Required));
        })
        .test("is-complete", t(ETLCodes.DogIsMissingChipOrTattoo), async function (value) {
          if (+masterRequestId === 0) {
            if (this.parent.lab === ELab.ZOOLYX) {
              if (value) {
                const res = await dogApi.hasDogGotChipsOrTattoo(dogIdValue);
                return res;
              }
            }
          }
          return true;
        })
        .test("dog-deceased", t(ETLCodes.DogIsDeceased), async function (value) {
          if (+masterRequestId === 0) {
            if (value) {
              const res = await dogApi.isDogDeceased(value);
              return !res.value;
            }
          }
          return true;
        })
        .test("is-not-excluded", t(ETLCodes.DogIsExcluded), async function (value) {
          if (+masterRequestId === 0) {
            if (value) {
              const result = await dogApi.isDogExcluded(value);
              return !result.value;
            }
          }
          return true;
        }),
      lab: Yup.string().nullable().required(t(ETLCodes.Required)),
      request: Yup.object().shape({
        personId: Yup.number()
          .nullable()
          .required(t(ETLCodes.Required))
          .test("is-not-deseased", t(ETLCodes.PersonISDeceased), async function (value) {
            if (+masterRequestId === 0) {
              if (healthTestRequestId <= 0 || data?.request?.status === ERequestState.REQUESTED) {
                const result = await personApi.isDeceased(value);
                return !result.value;
              }
            }
            return true;
          })
          .test("lab-missing-information", t(ETLCodes.MissingPhoneNumber), async function (value) {
            if (healthTestRequestId <= 0 || data?.request?.status === ERequestState.REQUESTED) {
              const result = await personApi.missingPhoneNumberCheck(value);
              return result.value;
            }
            return true;
          })
          .test("missing-adress-information", t(ETLCodes.MissingCompleteAdressOrEmail), async function (value) {
            if (healthTestRequestId <= 0 || data?.request?.status === ERequestState.REQUESTED) {
              if (value !== 0 && value !== null && value !== undefined) {
                const phoneNumber = await personApi.missingAddressInformationCheck(value);
                const address = await personApi.missingAddressInformationCheck(value);
                return phoneNumber.value && address.value;
              }
              return true;
            }
            return true;
          }),
        requestDate: Yup.date().nullable().required(t(ETLCodes.Required)),
      }),
    });
  }, [data?.request?.status, dogApi, dogIdValue, healthTestRequestId, masterRequestId, personApi, t]);

  const LabWorkFormSchema = React.useMemo(() => {
    return Yup.object().shape({
      dogId: Yup.number()
        .nullable()
        .test("dog-deceased", t(ETLCodes.DogIsDeceased), async function (value) {
          if (+masterRequestId === 0) {
            if (value) {
              const res = await dogApi.isDogDeceased(value);
              return !res.value;
            }
          }
          return true;
        })
        .test("is-not-excluded", t(ETLCodes.DogIsExcluded), async function (value) {
          if (+masterRequestId === 0) {
            if (value) {
              const result = await dogApi.isDogExcluded(value);
              return !result.value;
            }
          }
          return true;
        })
        .test("is-owner", t(ETLCodes.PersonIsNotOwner), async function (value) {
          if (+masterRequestId === 0) {
            if (value) {
              const res = await dogApi.isBelgianDogOwner(dogIdValue, data?.request?.personId);
              return res.value;
            }
          }
          return true;
        }),
    });
  }, [data?.request?.personId, dogApi, dogIdValue, masterRequestId, t]);

  const OtherFormSchema = React.useMemo(() => {
    return Yup.object().shape({});
  }, []);

  /* ========== Variables used for display rules ===========*/
  const editable = React.useMemo(
    () =>
      data?.request?.status === ERequestState.REQUESTED &&
      (requestInfo?.masterRequestStateCode === ERequestState.REQUESTED || requestInfo?.masterRequestStateCode == null),
    [data, requestInfo]
  );

  const dogEditable = React.useMemo(
    () =>
      data?.request?.status === ERequestState.REQUESTED ||
      (data?.dogId === null && data?.request?.status === ERequestState.LAB_WORK),
    [data]
  );

  const blocked = React.useMemo(
    () =>
      requestInfo?.isRequestBlocked ||
      requestInfo?.isMasterRequestBlocked ||
      requestInfo?.requestStateCode === ERequestState.EXECUTED ||
      (requestInfo?.masterRequestStateCode != null &&
        !(requestInfo?.masterRequestStateCode === ERequestState.REQUESTED)),
    [requestInfo]
  );
  const onCancel = React.useCallback(() => history.push(`${ERoutes.healthTestRequest}`), [history]);

  const requestApi = useApiService(RequestApi);
  const [shpLoading, setShpLoading] = React.useState(false);
  const existsInShp = React.useMemo(() => !!data?.request.sharepointUri, [data?.request]);
  const openOrCreateInShp = React.useCallback(async () => {
    setShpLoading(true);
    if (!!data?.request.sharepointUri) {
      window.open(data?.request.sharepointUri, "_blank");
      setShpLoading(false);
    } else {
      try {
        await requestApi.createInSharepoint(data?.requestId);
        refresh();
      } catch (e) {
        throw e;
      } finally {
        setShpLoading(false);
      }
    }
  }, [data?.request.sharepointUri, data?.requestId, requestApi, refresh]);

  const getBlockedIds = React.useCallback(
    async (dogId: any) => {
      var result = await api.getBlockedHealthTests(healthTestRequestId, dogId);
      setBlockedIds(result);
    },
    [api, healthTestRequestId]
  );

  return (
    <SmallFormGenerator
      initialValues={data}
      editMode={editMode}
      onSubmit={saveCheck}
      validationSchema={
        data?.request?.status === ERequestState.REQUESTED
          ? RequestedFormSchema
          : data?.request?.status === ERequestState.LAB_WORK
          ? LabWorkFormSchema
          : OtherFormSchema
      }
      loading={loading}
      saving={saving || saveChecking}
      onCancel={onCancel}
      hideButtons={data?.request?.status === ERequestState.EXECUTED}
    >
      <FGListen
        field="whiteTest"
        onChanged={(value, formik) => {
          setWhiteTest(value);
          if (value && !data?.dogId) {
            formik.setFieldValue("dogId", null);
          }
        }}
      />
      <FGListen
        field="lab"
        onChanged={(value, formik) => {
          if (value !== lab) {
            if (lab !== null && lab !== undefined) {
              formik.setFieldValue("healthPackIds", []);
            }
            setLab(value);
          }
          if (value === ELab.ZOOLYX) {
            formik.setFieldValue("whiteTest", false);
          }
          if (value && formik.values.dogId) {
            getBlockedIds(formik.values.dogId);
          }
        }}
      />
      <FGListen field="dogId" onChanged={(value) => setDogIdValue(value)} />
      <FieldSet
        title={t(ETLCodes.RequestData)}
        rightElement={
          healthTestRequestId > 0 && (
            <SharepointButton existsInShp={existsInShp} loading={shpLoading} onClick={openOrCreateInShp} />
          )
        }
      >
        <FieldGroup columns={2}>
          <FGSRSHDateMaskInput
            name="request.requestDate"
            label={t(ETLCodes.RequestDate)}
            requiredMark
            readonly={!editable || blocked || requestInfo?.masterRequestId > 0}
          />
          <FGSelectInput
            name="labStatusId"
            label={t(ETLCodes.LabStatus)}
            items={labStatuses}
            loading={lsloading}
            readonly
            visible={requestInfo?.requestStateCode !== ERequestState.REQUESTED}
            requiredMark
          />
        </FieldGroup>
        <FieldGroup columns={2}>
          <FGSRSHCheckboxInput
            name="whiteTest"
            label={t(ETLCodes.WhiteTest)}
            readonly={
              (data?.request?.status !== ERequestState.REQUESTED && data?.request?.status !== ERequestState.LAB_WORK) ||
              blocked ||
              requestInfo?.masterRequestId > 0
            }
            disabled={lab === ELab.ZOOLYX}
            visible={healthTestRequestId <= 0 || data?.request?.status === ERequestState.REQUESTED}
          />
          <FGTextAreaInput
            name="request.remark"
            label={t(ETLCodes.Remark)}
            readonly={data?.request?.status === ERequestState.EXECUTED}
          />
        </FieldGroup>
        <FieldGroup columns={2}>
          <FGDogSearchInput
            name="dogId"
            label={t(ETLCodes.Dog)}
            readonly={!dogEditable || +masterRequestId > 0}
            onItemViewClick={(item) => openNewTab(`${ERoutes.dog}/${item}/detail/view`)}
            requiredMark={!whiteTest || data?.request?.status === ERequestState.LAB_WORK}
            defaultSearch={{
              isForTempRSH: true,
            }}
          />
        </FieldGroup>
        <FieldGroup columns={2}>
          <FGPeopleSearchInput
            name="request.personId"
            label={t(ETLCodes.Contact)}
            readonly={!editable || blocked || requestInfo?.masterRequestId > 0}
            onItemViewClick={(item) => openNewTab(`${ERoutes.people}/${item}/detail/view`)}
            info={<InformationTooltip tooltipMessage={ETLCodes.MustBeBelgianOwner} />}
            requiredMark
            showWarning={true}
          />
        </FieldGroup>
        <FieldGroup columns={2}>
          <FGSelectInput
            name="lab"
            items={labs}
            loading={lloading}
            label={t(ETLCodes.Lab)}
            readonly={!editable || blocked || requestInfo?.masterRequestId > 0}
            requiredMark
          />
        </FieldGroup>
      </FieldSet>

      {lab === ELab.PROGENUS && (
        <FieldSet title={t(ETLCodes.HealthTests)}>
          <FGCustomPanel>
            {({ formik }) => (
              <HealthTestList
                onSelectionUpdated={(selected) => formik.setFieldValue("singleHealthTestIds", selected)}
                lab={lab}
                selectedIds={formik?.values?.singleHealthTestIds ? [...formik?.values?.singleHealthTestIds] : []}
                blockedIds={blockedIds?.blockedSingleHealthTestsIds}
                disabled={!editMode}
                healthTestRequestId={
                  requestInfo?.requestStateCode === ERequestState.REQUESTED ? null : healthTestRequestId
                }
              />
            )}
          </FGCustomPanel>
        </FieldSet>
      )}

      <FieldSet title={t(ETLCodes.HealthPack)}>
        <FGCustomPanel>
          {({ formik }) =>
            formik.values.lab ? (
              <HealthPacksList
                selectedIds={[...(formik.values?.healthPackIds ?? [])]}
                lab={formik.values.lab}
                dogId={dogIdValue ?? null}
                onSelectionUpdated={(selected) => formik.setFieldValue("healthPackIds", selected)}
                disabled={!editable}
                blockedIds={blockedIds?.blockedHealthTestPackIds}
                healthTestRequestId={
                  requestInfo?.requestStateCode === ERequestState.REQUESTED ? null : healthTestRequestId
                }
              />
            ) : (
              <NonIdealState icon="resolve" description={t(ETLCodes.PleaseSelectALab)} />
            )
          }
        </FGCustomPanel>
      </FieldSet>

      {data?.request?.status === ERequestState.EXECUTED && (
        <InlineButtonContainer>
          <div />
          <ButtonsBloc style={{ float: "right" }}>
            <CancelButton minimal={false} onClick={() => history.push(`${ERoutes.dysplasiaRequest}`)} />
          </ButtonsBloc>
        </InlineButtonContainer>
      )}
    </SmallFormGenerator>
  );
};
